import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useEffect } from 'react';
import { BehaviorSubject, interval } from 'rxjs';

const kPageMs = 10 * 1000;

/**
 * The screenful of `items` showing now: the pages turn by themselves, as
 * nobody touches the display. A reload that changes the page count turns
 * on from the page showing, not from the first.
 */
export function usePaged<T>(items: T[], perPage: number) {
  const pageCount =
    perPage > 0 ? Math.max(1, Math.ceil(items.length / perPage)) : 1;
  const page$ = useCreation(() => new BehaviorSubject(0), []);
  const [turned] = useBehaviorSubject(page$);
  useEffect(() => {
    const turns = interval(kPageMs).subscribe(() =>
      page$.next((Math.min(page$.getValue(), pageCount - 1) + 1) % pageCount),
    );
    return () => turns.unsubscribe();
  }, [page$, pageCount]);
  // Kept from a longer list until the next turn.
  const page = Math.min(turned, pageCount - 1);
  return {
    page,
    pageCount,
    items: perPage > 0 ? items.slice(page * perPage, (page + 1) * perPage) : [],
  };
}
